
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useUsers } from '@/api/users';
import { useGym } from '@/contexts/gym-context';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Search, User } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAssignUserToStaff } from '@/api/staff';

// Add a prop to AssignUsersDialog for onSuccess (to trigger staff refetch)
export interface AssignUsersDialogProps {
  staffId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void; // <-- add this
}

export function AssignUsersDialog({
  staffId,
  open,
  onOpenChange,
  onSuccess,
}: AssignUsersDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    name: string;
    email: string;
  } | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { selectedGym } = useGym();
  const { mutateAsync: assignUserToStaff, isPending } = useAssignUserToStaff();

  // Fetch users filtered by the selected gym
  const {
    data: users,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useUsers(selectedGym);

  // Map users to a consistent format
  const filteredUsers =
    users?.map((user) => ({
      id: user.id || user._id || '',
      name: user.name || '',
      email: user.email || '',
      surname: user.surname || '',
    })) || [];

  const handleUserSelect = (user: {
    id: string;
    name: string;
    email: string;
  }) => {
    setSelectedUser(user);
    setIsUserMenuOpen(false);
  };

  const handleAssign = async (userId: string) => {
    try {
      await assignUserToStaff({ staffId, userId });
      if (onSuccess) onSuccess();
      onOpenChange(false);
      setSelectedUser(null);
    } catch (error) {
      // error toast handled by hook
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('Assign Users to Staff')}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleAssign(selectedUser?.id || '');
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="user">{t('planAssignments.selectUser')}</Label>
            <Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={isUserMenuOpen}
                  className="w-full justify-between"
                  type="button"
                >
                  {selectedUser ? (
                    <div className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      <span>{selectedUser.name}</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-muted-foreground">
                      <Search className="mr-2 h-4 w-4" />
                      <span>{t('planAssignments.selectUser')}</span>
                    </div>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[400px] p-0">
                <Command>
                  <CommandInput
                    placeholder={t('planAssignments.searchUsers')}
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                  />
                  <CommandList>
                    <CommandEmpty>
                      {isLoadingUsers
                        ? 'Loading users...'
                        : t('planAssignments.noUsersFound')}
                    </CommandEmpty>
                    {isLoadingUsers ? (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                      </div>
                    ) : (
                      <ScrollArea className="h-[200px]">
                        <CommandGroup>
                          {filteredUsers.length > 0
                            ? filteredUsers
                                .filter(
                                  (user) =>
                                    user.name
                                      .toLowerCase()
                                      .includes(searchQuery.toLowerCase()) ||
                                    user.email
                                      .toLowerCase()
                                      .includes(searchQuery.toLowerCase())
                                )
                                .map((user) => (
                                  <CommandItem
                                    key={user.id}
                                    value={user.name}
                                    onSelect={() => handleUserSelect(user)}
                                  >
                                    <div className="flex flex-col">
                                      <span className="font-medium">
                                        {user.name} {user.surname || ''}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {user.email}
                                      </span>
                                    </div>
                                  </CommandItem>
                                ))
                            : null}
                        </CommandGroup>
                      </ScrollArea>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={!selectedUser || isPending}>
              {isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
